import { Component, EventEmitter, Injector, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IAnnualReport, IFinStatements } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import _ from 'lodash';
import { Observable, OperatorFunction, Subject, map, takeUntil, tap } from 'rxjs';
import {
    EMPTY_ARRAY_LENGTH,
    IDialogDataWithAction,
    NotificationType,
    OkCancelDialogActions,
} from '../../../../../../../../aumico-lib-core-fe/src/lib';
import { ConfirmationModal } from '../../../../../../../../aumico-lib-notes-fe/src/lib/components/confirmation-modal/confirmation-modal.component';
import { ArFeatureBaseAdapter } from '../../../../../_base/arFeatureBase.page';
import { ArFeatureDataState } from '../../../../../_base/arFeatureDataState.enum';
import FcpHelper from '../../domain/helpers/fcp.hepler';
import { FcpTreeItemMapper } from '../../domain/mappers/fcpTreeItem.mapper';
import { FcpModel } from '../../domain/models/fcp.model';
import { FcpTreeItem } from '../../domain/models/fcpTreeItem.model';
import { FcpStateServiceAdapter } from '../outbound/fcpState.service';
@UntilDestroy()
@Component({
    selector: 'aumico-financial-closing-process-adapter',
    templateUrl: 'fcp.adapter.html',
    styleUrls: ['fcp.adapter.scss'],
})
export class FinancialClosingProcessAdapter extends ArFeatureBaseAdapter<FcpModel> implements OnInit, OnDestroy {
    private dialog: MatDialog;
    private destroy$ = new Subject<void>();
    private markedForAutoUpdate = false;
    private reloadExecuted: boolean;

    @Output() finalizeAndContinueButtonClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(injector: Injector, stateService: FcpStateServiceAdapter) {
        super(injector, stateService);
        this.dialog = injector.get(MatDialog);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.annualReportProvider
            .getActiveOriginalFinStatements$()
            .pipe(this.checkForChanges(), takeUntil(this.destroy$))
            .subscribe();
    }

    ngOnDestroy() {
        // Emit a value to destroy all subscriptions
        this.destroy$.next();
        // Complete the subject to free up memory
        this.destroy$.complete();
    }

    // Pick the Data from saved Annual Report which are saved for this feature
    override takeSavedData(original: IAnnualReport) {
        // Here we pick the data we need from IAnnualReport
        if (original.fcp == undefined) {
            return false;
        } else {
            this.domainModel = FcpModel.fromJson(original.fcp);
            //here we should load what's saved instead of creating new tree items.
            this.domainModel.items = this.createTreeItemsIfNeeded(this.domainModel);

            return true;
        }
    }

    // Pick the Data from Annual Report previous year
    override handleAutofillFromOtherAnnualReport(
        annualReportForAutofill: IAnnualReport,
        finStatementsForAutofill: IFinStatements[],
        currentYearAnnualReport: IAnnualReport,
        currentYearFinStatements: IFinStatements[],
        overwrite: boolean,
    ): boolean {
        try {
            this.checkPreconditons(annualReportForAutofill);
        } catch (error) {
            return false;
        }

        this.markedForAutoUpdate = true;
        if (annualReportForAutofill.fcp == null) {
            //In this case we cannot initilize it with previous year data
            return false;
        }
        this.domainModel = FcpModel.fromJson(annualReportForAutofill.fcp);
        this.domainModel = FcpHelper.autofillDomainModelFromOtherAnnualReport(
            this.domainModel,
            this.domainModel.inputFinStatements,
            new Date(currentYearAnnualReport.generalInformation.balanceSheetDate),
            new Date(annualReportForAutofill.generalInformation.balanceSheetDate),
        );
        return true;
    }

    // Pick the Data from Annual Report current year if possible
    override handleAutofillFromCurrentYear(
        original: IAnnualReport,
        originalFinStatements: IFinStatements[],
        overwrite: boolean,
    ) {
        // It's fine to access directly the .finStatements of annual report because here we need to take
        // the data from the current year and we are creating the new data for fcp

        this.checkPreconditons(original);
        if (original.fcp == null) {
            const { version } = original.finStatements?.[0];
            this.domainModel = new FcpModel(original.finStatements, original.finStatements, [], [], version);
            this.domainModel.items = FcpTreeItemMapper.mapFinstatementToTreeItems(original.finStatements);
        } else {
            throw new Error('cannot overwrite already saved data');
        }
    }

    private checkPreconditons(original: IAnnualReport) {
        if (original == null) {
            // Not possible, error
            throw new Error('original is null');
        }
        if (original.finStatements == null || original.finStatements.length == 0) {
            // Not possible, error
            throw new Error('original is missing financial statements');
        }
    }

    private createTreeItemsIfNeeded(domainModel: FcpModel): FcpTreeItem[] {
        if (
            domainModel.items.length == 0 &&
            domainModel.inputFinStatements.length > 0 &&
            domainModel.inputFinStatements[0].templateAndAccountsMapping.template
        ) {
            return FcpTreeItemMapper.mapFinstatementToTreeItems(domainModel.inputFinStatements);
        }
        return domainModel.items;
    }

    private checkForChanges(): OperatorFunction<IFinStatements[], void> {
        return (source$: Observable<IFinStatements[]>) =>
            source$.pipe(
                tap((finStatementFromMapping: IFinStatements[]) => {
                    //Domain model must be initialized before we can check for changes and this code will be called again if necessary.
                    if (this.domainModel === undefined) {
                        return;
                    }
                    if (finStatementFromMapping.length === EMPTY_ARRAY_LENGTH) {
                        this.stateService.updateState(ArFeatureDataState.ERROR);
                        return;
                    }

                    const inputFinStatements = this.domainModel.inputFinStatements;
                    if (!this.areStatementsEqual(inputFinStatements, finStatementFromMapping)) {
                        if (this.markedForAutoUpdate) {
                            this.markedForAutoUpdate = false;
                            this.domainModel = FcpHelper.reloadDomainModel(this.domainModel, finStatementFromMapping);
                        } else if (!this.reloadExecuted) {
                            //this.showReloadDialog(finStatementFromMapping);
                            // always relead the domain model if the data has changed
                            this.domainModel = FcpHelper.reloadDomainModel(this.domainModel, finStatementFromMapping);
                            this.notifyChanges();
                            this.reloadExecuted = true;
                        }
                    }
                }),
                map(() => void 0), // Transform the observable to void
            );
    }

    private areStatementsEqual(statements1: IFinStatements[], statements2: IFinStatements[]): boolean {
        // Implement comparison logic based on your data structure
        const isEqual = _.isEqual(statements1, statements2);
        if (!isEqual) {
            this.findDifferences(statements1, statements2);
        }
        return isEqual;
    }

    findDifferences(obj1, obj2, path = ''): boolean {
        if (!_.isEqual(obj1, obj2)) {
            if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
                console.log(`Difference at ${path}: ${obj1} !== ${obj2}`);
                return true; // Return true to indicate a difference was found
            }

            const keys1 = Object.keys(obj1);
            const keys2 = Object.keys(obj2);

            // Check properties in obj1
            for (let key of keys1) {
                const newPath = path ? `${path}.${key}` : key;
                if (!(key in obj2)) {
                    console.log(`Difference at ${newPath}: key missing in second object`);
                    return true; // Return true to indicate a difference was found
                } else if (this.findDifferences(obj1[key], obj2[key], newPath)) {
                    return true; // If a difference is found in the deeper levels, stop further execution
                }
            }

            // Check properties in obj2 that are not in obj1
            for (let key of keys2) {
                const newPath = path ? `${path}.${key}` : key;
                if (!(key in obj1)) {
                    console.log(`Difference at ${newPath}: key missing in first object`);
                    return true; // Return true to indicate a difference was found
                }
            }
        }

        return false; // No difference found
    }

    private notifyChanges(): void {
        this.coreProvider.dispatchAddNotification(
            'aumico_reports_annualreport_fcp_input_data_has_changed_notification_title',
            'aumico_reports_annualreport_fcp_input_data_has_changed_notification_text',
            NotificationType.SUCCESS,
        );
    }

    private showReloadDialog(finStatementFromMapping: IFinStatements[]): void {
        const data: IDialogDataWithAction = {
            contentMessage: 'aumico_reports_annualreport_fcp_input_data_has_changed_dialog_text',
            titleLocalizationKey: 'aumico_reports_annualreport_fcp_input_data_has_changed_dialog_title',
            actionBtnTextKey: 'aumico_reports_annualreport_fcp_input_data_has_changed_dialog_action_button',
        };
        const dialogRef = this.dialog.open(ConfirmationModal, {
            width: '400px',
            data,
        });

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result === OkCancelDialogActions.OK) {
                    this.domainModel = FcpHelper.reloadDomainModel(this.domainModel, finStatementFromMapping);
                }
                this.reloadExecuted = true;
            });
    }

    public finalizeAndContinue(): void {
        this.finalizeAndContinueButtonClicked.emit(true);
    }

    onUserSaveDomainModel(): void {
        this.stateService.updateState(ArFeatureDataState.INITILIZED_FROM_SAVED_DATA);
    }
}
